import React, { useMemo } from 'react';
import { useGame } from '../context/GameContext';
import { AvatarChip } from './Avatar';
import { Trophy, Skull, Crown, X } from 'lucide-react';

// v2.3 PvP RESULTS OVERLAY — fires when race is concluded:
// - any member.status === 'won' (boss defeated), OR
// - every playing member is now 'lost'
// Non-blocking overlay; player dismisses to return to menu / lobby.

export default function PartyResults() {
  const { partyState, playerName, setScreen, setPartyState } = useGame();
  const [dismissed, setDismissed] = React.useState(false);

  const raceOver = useMemo(() => {
    if (!partyState) return false;
    const members = partyState.members || [];
    if (members.length < 2) return false;
    const anyWon = members.some(m => m.status === 'won' || m.boss_defeated);
    const engaged = members.filter(m => ['playing','won','lost'].includes(m.status));
    if (engaged.length < 2) return false;
    const allDone = engaged.every(m => m.status === 'won' || m.status === 'lost');
    return anyWon || allDone;
  }, [partyState]);

  if (!raceOver || dismissed || !partyState) return null;

  const members = partyState.members || [];
  const myTag = (playerName || '').toUpperCase();
  const ranked = [...members].sort((a, b) => {
    // Won > higher floor > higher score
    const aWon = a.status === 'won' || a.boss_defeated;
    const bWon = b.status === 'won' || b.boss_defeated;
    if (aWon !== bWon) return aWon ? -1 : 1;
    if (b.floor !== a.floor) return b.floor - a.floor;
    return (b.score || 0) - (a.score || 0);
  });
  const winner = ranked[0];
  const myRank = ranked.findIndex(m => m.tag === myTag) + 1;
  const iWon = winner && winner.tag === myTag;

  const goToLobby = () => {
    setDismissed(true);
    setScreen('party');
  };
  const goToMenu = () => {
    setDismissed(true);
    setPartyState(null);
    setScreen('menu');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-sm p-4" data-testid="pvp-results-overlay">
      <div className="brutal-border bg-[#0a0a0a] max-w-2xl w-full p-8 relative" style={{ borderColor: iWon ? 'var(--neon)' : 'var(--pink)', boxShadow: `8px 8px 0 ${iWon ? 'var(--neon)' : 'var(--pink)'}` }}>
        <button
          onClick={goToLobby}
          className="absolute top-3 right-3 text-[color:var(--text-dim)] hover:text-white"
          data-testid="pvp-results-close"
          aria-label="close"
        >
          <X size={20}/>
        </button>
        <div className="text-center mb-6">
          <div className="text-[10px] uppercase tracking-[0.4em] text-[color:var(--text-dim)]">RACE FINISHED</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl uppercase mt-2" style={{ color: iWon ? 'var(--neon)' : 'var(--pink)' }} data-testid="pvp-results-heading">
            {iWon ? 'YOU WON' : `${winner?.tag || 'SOMEONE'} WON`}
          </h1>
          {myRank > 0 && (
            <div className="text-xs uppercase font-mono text-[color:var(--text-dim)] mt-2" data-testid="pvp-results-my-rank">
              YOUR PLACE · #{myRank} of {ranked.length}
            </div>
          )}
        </div>

        <div className="space-y-2" data-testid="pvp-results-list">
          {ranked.map((m, i) => {
            const isMe = m.tag === myTag;
            const won = m.status === 'won' || m.boss_defeated;
            const lost = m.status === 'lost';
            return (
              <div
                key={m.tag}
                className="flex items-center gap-3 p-3 brutal-border-dim"
                data-testid={`pvp-results-row-${m.tag}`}
                style={{ borderColor: isMe ? 'var(--neon)' : '#333' }}
              >
                <div className="font-display font-black text-2xl w-10 text-center" style={{ color: i === 0 ? 'var(--pink)' : 'var(--text-dim)' }}>
                  #{i + 1}
                </div>
                <AvatarChip avatar={m.avatar} frame={m.avatar?.frame} size={36} testid={`pvp-results-avatar-${m.tag}`} />
                <div className="flex-1 min-w-0">
                  <div className="font-display font-black uppercase truncate flex items-center gap-1">
                    {m.tag}
                    {won && <Trophy size={14} className="text-[color:var(--pink)] shrink-0"/>}
                    {lost && !won && <Skull size={14} className="text-[color:var(--red)] shrink-0"/>}
                    {i === 0 && !won && !lost && <Crown size={14} className="text-[color:var(--pink)] shrink-0"/>}
                  </div>
                  <div className="text-[10px] font-mono uppercase text-[color:var(--text-dim)]">
                    F{m.floor || 0} · {m.score || 0} PTS · {won ? 'VICTORY' : lost ? 'DIED' : m.status}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-6 flex gap-3 justify-center">
          <button className="brutal-btn text-sm px-6 py-3" onClick={goToLobby} data-testid="pvp-results-lobby">
            BACK TO LOBBY
          </button>
          <button className="brutal-btn text-sm px-6 py-3" onClick={goToMenu} data-testid="pvp-results-menu" style={{ borderColor: 'var(--pink)', color: 'var(--pink)' }}>
            MAIN MENU
          </button>
        </div>
      </div>
    </div>
  );
}
